import {UIIcon} from '@dropbox/dig-icons';
import {
  CalendarLine,
  CodeLine,
  LibraryLine,
  PersonMultipleLine,
  TableLine,
} from '@dropbox/dig-icons/assets';
import {DashGlyph} from '@dropbox/dig-logos';
import confluenceLogo from 'assets/logos/confluence.svg';
import dropboxLogo from 'assets/logos/dropbox.svg';
import googleDriveLogo from 'assets/logos/google_drive.svg';
import jiraLogo from 'assets/logos/jira.svg';
import paperLogo from 'assets/logos/paper.svg';
import slackLogo from 'assets/logos/slack.svg';
import {CompanyOSGlyph} from 'components/DSYS/CompanyOSLogo';
import {ReactNode} from 'react';

import styles from './aiQueryConversation.module.css';
import type {DocumentSource} from './aiQueryReadDocument';

/**
 * The CompanyOS glyph in color, at the size and tight box of Ask's working
 * glyph (13px), so a line that starts on the glyph doesn't jump when its icon
 * takes over: the glyph just stops moving.
 */
const CompanyOSMark = () => <CompanyOSGlyph width={13} height={13} viewBox="9.14288 7 18 18" />;

/** Dash's glyph in Dropbox blue, cropped to its art so it reads at the same size. */
const DashMark = () => (
  <DashGlyph className={styles.dashMark} width={13} height={13} viewBox="12.5 12.5 39 39" />
);

/**
 * A brand mark in its own colors. dig-icons' Slack and Jira logos take the
 * line's text color, so these are the full-color SVGs the app uses elsewhere.
 */
const Logo = ({src, label}: {src: string; label: string}) => (
  <img src={src} alt={label} width={16} height={16} style={{display: 'block'}} />
);

/** A document read_document opens, by where it lives. */
const DOCUMENT_LOGOS: Record<DocumentSource, () => ReactNode> = {
  confluence: () => <Logo src={confluenceLogo} label="Confluence" />,
  paper: () => <Logo src={paperLogo} label="Paper" />,
  drive: () => <Logo src={googleDriveLogo} label="Google Drive" />,
  help_center: () => <Logo src={dropboxLogo} label="Dropbox Help Center" />,
  dropbox: () => <Logo src={dropboxLogo} label="Dropbox" />,
};

/** Tools that look somewhere other than CompanyOS's own data, by where they look. */
const ICONS: Record<string, () => ReactNode> = {
  ask_dash: () => <DashMark />,
  search_docs: () => <DashMark />,
  read_document: () => <DashMark />,
  find_knowledge_articles: () => <UIIcon src={LibraryLine} size="small" />,
  get_knowledge_article_detail: () => <UIIcon src={LibraryLine} size="small" />,
  find_slack_messages: () => <Logo src={slackLogo} label="Slack" />,
  find_calendar_events: () => <UIIcon src={CalendarLine} size="small" />,
  find_jira_items: () => <Logo src={jiraLogo} label="Jira" />,
  search_code: () => <UIIcon src={CodeLine} size="small" />,
  query_directory: () => <UIIcon src={PersonMultipleLine} size="small" />,
};

/**
 * Where a step looks, for its line: CompanyOS's own data, Dash (and the docs it
 * searches), Slack and Jira in their colors; a document it reads by where it
 * lives (`documentSource`: Confluence, Paper, Drive, Dropbox); Knowledge
 * (ServiceNow articles), the calendar, code, the company directory and the
 * agent's own bookkeeping, which have no logo, as DIG icons.
 */
export const stepIcon = (
  toolName: string,
  isBookkeeping: boolean,
  documentSource?: DocumentSource
): ReactNode =>
  isBookkeeping ? (
    <UIIcon src={TableLine} size="small" />
  ) : documentSource ? (
    DOCUMENT_LOGOS[documentSource]()
  ) : (
    (ICONS[toolName]?.() ?? <CompanyOSMark />)
  );
